import {
  GATECHECK_DECISION_STATES,
  getGatecheckRecord,
  listGatecheckDemos,
} from "../data/gatecheckDemos";

const defaultFinding = () => ({
  id: "",
  type: "CLARIFICATION",
  severity: "Medium",
  title: "",
  explanation: "",
  tenderRequirement: "",
  tenderSource: "",
  clauseReference: "",
  contractorEvidence: "",
  contractorEvidenceSource: "",
  whyItMatters: "",
  requiredAction: "",
  owner: "",
  status: "Unresolved",
  confidence: "Medium",
  notes: "",
});

const defaultAction = () => ({
  owner: "",
  action: "",
  dueLabel: "",
  status: "Open",
  linkedFinding: "",
});

const defaultHistoryEntry = () => ({
  date: "",
  state: "HOLD",
  owner: "",
  note: "",
  origin: "",
});

export function createEmptyContractorProfile() {
  return {
    profileId: "",
    contractorName: "",
    contractorShortName: "",
    sector: "Specialist contractor",
    legalName: "",
    website: "",
    location: "",
    regionsServed: "",
    scopesDelivered: "",
    certifications: "",
    insurance: "",
    referenceProjects: "",
    workforceCapacity: "",
    qaEvidence: "",
    strengths: "",
    unknowns: "",
  };
}

export function createEmptyTenderProfile() {
  return {
    projectName: "",
    projectSubtitle: "Pre-estimate gate",
    client: "",
    geography: "",
    estimatedValue: "",
    deadline: "",
    packageScope: "",
    sector: "",
    deliveryModel: "",
    occupiedBuilding: true,
    mainContractorLed: true,
    requirements: "",
    exclusions: "",
    notes: "",
  };
}

export function createEmptyDecisionDraft() {
  return {
    brand: "Layerbound",
    badge: "Manual Gatecheck",
    status: "HOLD",
    recommendation: "Resolve the items below before estimating starts.",
    rationale: "",
    confidence: "",
    humanDecision: "",
    decisionOwner: "",
    approvalStatus: "",
    overrideReason: "",
    opportunitySummary: "",
    lastUpdated: new Date().toISOString(),
    findings: [defaultFinding()],
    nextActions: [defaultAction()],
    decisionHistory: [defaultHistoryEntry()],
  };
}

function makeId(value, fallback) {
  const slug = String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return slug || fallback;
}

function summariseExecutiveCounts(findings) {
  const counts = findings.reduce(
    (acc, finding) => {
      const type = String(finding.type || "").toUpperCase();
      if (type === "BLOCKER") acc.blockers += 1;
      if (type === "EVIDENCE GAP") acc.evidenceGaps += 1;
      if (type === "SCOPE DECISION") acc.scopeDecisions += 1;
      return acc;
    },
    { blockers: 0, evidenceGaps: 0, scopeDecisions: 0 }
  );

  return [
    { label: "Blockers", value: counts.blockers },
    { label: "Evidence gaps", value: counts.evidenceGaps },
    { label: "Scope decisions", value: counts.scopeDecisions },
  ];
}

export function buildGatecheckRecord(contractorProfile, tenderProfile, decisionDraft) {
  const findings = (decisionDraft.findings || [])
    .map((finding, index) => ({
      ...defaultFinding(),
      ...finding,
      id: finding.id || makeId(finding.title, `finding-${index + 1}`),
    }))
    .filter((finding) => finding.title.trim());
  const nextActions = (decisionDraft.nextActions || [])
    .map((action) => ({ ...defaultAction(), ...action }))
    .filter((action) => action.action.trim());
  const decisionHistory = (decisionDraft.decisionHistory || [])
    .map((entry) => ({ ...defaultHistoryEntry(), ...entry }))
    .filter((entry) => entry.date || entry.note.trim() || entry.owner.trim());

  return {
    brand: decisionDraft.brand || "Layerbound",
    badge: decisionDraft.badge || "Manual Gatecheck",
    contractorName: contractorProfile.contractorName,
    contractorShortName:
      contractorProfile.contractorShortName || contractorProfile.contractorName,
    sector:
      contractorProfile.sector ||
      tenderProfile.sector ||
      "Specialist contractor",
    projectName: tenderProfile.projectName,
    projectSubtitle: tenderProfile.projectSubtitle || "Pre-estimate gate",
    opportunitySummary:
      decisionDraft.opportunitySummary ||
      tenderProfile.notes ||
      tenderProfile.packageScope,
    lastUpdated: decisionDraft.lastUpdated || new Date().toISOString(),
    decision: {
      status: decisionDraft.status || "HOLD",
      recommendation:
        decisionDraft.recommendation ||
        "Resolve the items below before estimating starts.",
      rationale: decisionDraft.rationale || "",
      confidence: decisionDraft.confidence || "",
    },
    decisionOwnership: {
      humanDecision: decisionDraft.humanDecision || "",
      decisionOwner: decisionDraft.decisionOwner || "",
      approvalStatus: decisionDraft.approvalStatus || "",
      overrideReason: decisionDraft.overrideReason || "",
    },
    decisionHistory,
    tenderMeta: {
      deadline: tenderProfile.deadline || "",
      client: tenderProfile.client || "",
      estimatedValue: tenderProfile.estimatedValue || "",
      geography: tenderProfile.geography || "",
      scope: tenderProfile.packageScope || "",
    },
    executiveSummary: summariseExecutiveCounts(findings),
    nextActions,
    findings,
  };
}

export function buildTemplateMetaFromRecord(record) {
  const decision = record?.decision || {};
  return {
    flowType: "gatecheck",
    brandName: record?.brand || "Layerbound",
    headline: `${record?.projectName || "Gatecheck"} — ${record?.projectSubtitle || "Pre-estimate gate"}`,
    metaDescription:
      decision.recommendation ||
      "Pre-estimate decision link for specialist contractor qualification.",
    gatecheckRecord: record,
  };
}

export function validateGatecheckWorkspace(contractorProfile, tenderProfile, decisionDraft) {
  const errors = [];
  if (!contractorProfile.contractorName.trim()) {
    errors.push("Contractor name is required.");
  }
  if (!tenderProfile.projectName.trim()) {
    errors.push("Project name is required.");
  }
  if (!tenderProfile.packageScope.trim()) {
    errors.push("Package scope is required.");
  }
  if (!decisionDraft.status.trim()) {
    errors.push("Decision status is required.");
  }
  if (!decisionDraft.recommendation.trim()) {
    errors.push("Decision recommendation is required.");
  }
  const hasFinding = (decisionDraft.findings || []).some((finding) =>
    String(finding.title || "").trim()
  );
  if (!hasFinding) {
    errors.push("Add at least one gatecheck finding.");
  }
  const hasAction = (decisionDraft.nextActions || []).some((action) =>
    String(action.action || "").trim()
  );
  if (!hasAction) {
    errors.push("Add at least one next action.");
  }
  return errors;
}

export function getGatecheckPresetOptions() {
  return listGatecheckDemos().map((demo) => ({
    key: `${demo.contractorSlug}__${demo.projectSlug}`,
    ...demo,
  }));
}

export function createWorkspaceFromDemo(contractorSlug, projectSlug) {
  const record = getGatecheckRecord(contractorSlug, projectSlug);
  if (!record) {
    return {
      contractorProfile: createEmptyContractorProfile(),
      tenderProfile: createEmptyTenderProfile(),
      decisionDraft: createEmptyDecisionDraft(),
      storeSlug: "gatecheck-demo",
    };
  }

  const contractorProfile = {
    ...createEmptyContractorProfile(),
    contractorName: record.contractorName || "",
    contractorShortName: record.contractorShortName || "",
    sector: record.sector || "Specialist contractor",
    regionsServed: record.tenderMeta?.geography || "",
    scopesDelivered: record.tenderMeta?.scope || "",
    strengths: (record.findings || [])
      .filter((finding) => finding.type === "CLEAR")
      .map((finding) => finding.title)
      .join("\n"),
    unknowns: (record.findings || [])
      .filter((finding) => finding.type === "EVIDENCE GAP")
      .map((finding) => finding.title)
      .join("\n"),
    qaEvidence: (record.findings || [])
      .filter((finding) => /qa|report/i.test(finding.title || ""))
      .map((finding) => finding.contractorEvidence)
      .join("\n"),
  };

  const tenderProfile = {
    ...createEmptyTenderProfile(),
    projectName: record.projectName || "",
    projectSubtitle: record.projectSubtitle || "Pre-estimate gate",
    client: record.tenderMeta?.client || "",
    geography: record.tenderMeta?.geography || "",
    estimatedValue: record.tenderMeta?.estimatedValue || "",
    deadline: record.tenderMeta?.deadline || "",
    packageScope: record.tenderMeta?.scope || "",
    sector: record.sector || "",
    occupiedBuilding: /occupied/i.test(record.opportunitySummary || ""),
    mainContractorLed: true,
    requirements: (record.findings || [])
      .map((finding) => finding.tenderRequirement)
      .filter(Boolean)
      .join("\n"),
    notes: record.opportunitySummary || "",
  };

  const decisionDraft = {
    ...createEmptyDecisionDraft(),
    brand: record.brand || "Layerbound",
    badge: record.badge || "Manual Gatecheck",
    status: record.decision?.status || "HOLD",
    recommendation:
      record.decision?.recommendation ||
      "Resolve the items below before estimating starts.",
    rationale: record.decision?.rationale || "",
    confidence: record.decision?.confidence || "",
    humanDecision: record.decisionOwnership?.humanDecision || "",
    decisionOwner: record.decisionOwnership?.decisionOwner || "",
    approvalStatus: record.decisionOwnership?.approvalStatus || "",
    overrideReason: record.decisionOwnership?.overrideReason || "",
    opportunitySummary: record.opportunitySummary || "",
    lastUpdated: record.lastUpdated || new Date().toISOString(),
    findings: (record.findings || []).map((finding) => ({ ...defaultFinding(), ...finding })),
    nextActions: (record.nextActions || []).map((action) => ({ ...defaultAction(), ...action })),
    decisionHistory: (record.decisionHistory || []).map((entry) => ({
      ...defaultHistoryEntry(),
      ...entry,
    })),
  };

  return {
    contractorProfile,
    tenderProfile,
    decisionDraft,
    storeSlug: `gate-${projectSlug}`,
  };
}

export function duplicateFinding() {
  return defaultFinding();
}

export function duplicateAction() {
  return defaultAction();
}

export function duplicateHistoryEntry() {
  return defaultHistoryEntry();
}

export const GATECHECK_ALLOWED_DECISIONS = GATECHECK_DECISION_STATES;
